import * as Turbo from '@hotwired/turbo';
import { Application } from '@hotwired/stimulus';
import 'banzai/pendant/front/shared/global.js';
import 'banzai/pendant/front/shared/video-modal.js';
import Alpine from 'alpinejs';
import anchor from '@alpinejs/anchor';
import morph from '@alpinejs/morph';
import htmx from 'htmx.org';

// Make htmx and Alpine available globally
window.htmx = htmx;
window.Alpine = Alpine;

// Process htmx attributes after Turbo navigations
document.addEventListener('turbo:load', () => {
  htmx.process(document.body);
});

import FormState from '@pendant/ui/form-state';
import DragSort from '@pendant/ui/drag-sort';

import { registerIconLibrary } from '@shoelace-style/shoelace/dist/utilities/icon-library.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';

registerIconLibrary('default', {
  resolver: (name) => `https://static-app-misc.banzai.org/icon-library/${name}.svg`,
});

import ResourceController from 'banzai/pendant/front/resource/controller.js';
import BenefitController from 'banzai/pendant/front/benefit/controller.js';
import NavController from 'banzai/pendant/front/shared/nav.js';
import FlyoutController from 'banzai/pendant/front/shared/flyout.js';
import FormsController from 'banzai/pendant/front/shared/components/forms.js';
import AutocompleteController from 'banzai/pendant/front/shared/components/autocomplete.js';
import SendController from 'banzai/pendant/front/send/controller.js';
import CheckboxController from 'banzai/pendant/front/shared/components/checkbox.js';
import ButtonController from 'banzai/pendant/front/shared/components/button.js';
import ModalController from 'banzai/pendant/front/shared/modal.js';
import DiscussController from 'banzai/pendant/front/discuss/controller.js';
import NotificationsController from 'banzai/pendant/front/notifications/controller.js';
import PopupController from 'banzai/pendant/front/shared/popup.js';
import RearrangeableListController from 'banzai/pendant/front/shared/components/rearrangeable_list/controller.js';
import ShortcutsController from 'banzai/pendant/front/shared/shortcuts.js';
import SearchController from 'banzai/pendant/front/search/controller.js';
import KeyboardListController from 'banzai/pendant/front/shared/keyboard-list.js';
import GroupsCounterController from 'banzai/pendant/front/groups/counter.js';
import GroupsRecruitingController from 'banzai/pendant/front/groups/recruiting.js';
import GroupsChecklistsController from 'banzai/pendant/routes/groups/controller.js';
import InputController from 'banzai/pendant/front/shared/components/input.js';
import AnnounceController from 'banzai/pendant/front/announce/view/controller.js';
import VocabController from 'banzai/pendant/front/vocab/controller.js';
import DateController from 'banzai/pendant/front/shared/date-controller.js';
import ChecklistController from 'banzai/pendant/front/checklist/controller.js';
import StickyHeaderController from 'banzai/pendant/front/shared/sticky-header.js';
import CopyController from 'banzai/pendant/front/shared/components/copy.js';
import OrgController from 'banzai/pendant/front/orgs/org.js';
import UpvoteController from 'banzai/pendant/front/benefit/pages/upvote.js';
import VisitorEventsController from 'banzai/pendant/front/visitor_events/controller.js';
import AnalyticsFiltersController from 'banzai/pendant/front/analytics/controllers/analytics_filters_controller.js';
import AnalyticsChartController from 'banzai/pendant/front/analytics/controllers/chart_controller.js';
import QuestionTypeController from 'banzai/pendant/front/survey/view/question_type_controller.js';
import MultipleChoiceController from 'banzai/pendant/front/survey/view/multiple_choice_controller.js';
import AudienceController from 'banzai/pendant/front/survey/view/audience_controller.js';
import BookmarkController from 'banzai/pendant/routes/bookmarks/controller.js';
import FreeController from 'banzai/pendant/routes/free/controller.js';
import PageVersionController from 'banzai/pendant/routes/benefits/pages/versions/controller.js';

import '@pendant/routes/assistant/assistant.ts';

// Stimulus setup
const app = Application.start();

app.register('nav', NavController);
app.register('flyout', FlyoutController);
app.register('modal', ModalController);
app.register('resource', ResourceController);
app.register('benefit', BenefitController);
app.register('forms', FormsController);
app.register('autocomplete', AutocompleteController);
app.register('send', SendController);
app.register('checkbox', CheckboxController);
app.register('button', ButtonController);
app.register('discuss', DiscussController);
app.register('notifications', NotificationsController);
app.register('rearrangeable_list', RearrangeableListController);
app.register('shortcuts', ShortcutsController);
app.register('search', SearchController);
app.register('keyboard-list', KeyboardListController);
app.register('groups-counter', GroupsCounterController);
app.register('groups-recruiting', GroupsRecruitingController);
app.register('groups-checklists', GroupsChecklistsController);
app.register('announce', AnnounceController);
app.register('vocab', VocabController);
app.register('date', DateController);
app.register('checklist', ChecklistController);
app.register('sticky-header', StickyHeaderController);
app.register('popup', PopupController);
app.register('input', InputController);
app.register('copy', CopyController);
app.register('org', OrgController);
app.register('upvote', UpvoteController);
app.register('visitor-events', VisitorEventsController);
app.register('analytics-filters', AnalyticsFiltersController);
app.register('analytics-chart', AnalyticsChartController);
app.register('question-type', QuestionTypeController);
app.register('multiple-choice', MultipleChoiceController);
app.register('audience', AudienceController);
app.register('bookmark', BookmarkController);
app.register('free', FreeController);
app.register('page-version', PageVersionController);

document.addEventListener('alpine:init', () => {
  // setup global directives
  Alpine.directive('form-state', FormState);

  // Benefit wizard form component — registered here (before Alpine.start)
  // so it's available when Alpine processes x-data attributes.
  Alpine.data('wizardForm', (initial = {}) => ({
    selectedGroups: initial.selectedGroups || [],
    docCount: initial.docCount || 0,
    uploadingCount: 0,
    benefitsText: initial.benefitsText || '',
    get isValid() {
      return (
        this.benefitsText.trim().length > 0 &&
        this.selectedGroups.length > 0 &&
        this.docCount > 0 &&
        this.uploadingCount === 0
      );
    },
  }));
});

// ---------------------------------------------------------------------------
// Deferred Alpine initialization for lazy-loaded modules.
//
// Problem: Some Alpine components are registered by lazy-loaded ES modules
// (e.g., ui/trix.js) that may execute before or after Alpine.start().
// <script type="module"> only executes once per URL, so on Turbo
// navigations the module won't re-run, but new DOM elements with those
// components may appear via Turbo page visits, streams, or frame renders.
//
// Solution: Server-rendered elements use `x-ignore` (so Alpine.start()
// skips them) plus `data-alpine-defer="<name>"` to mark which module
// they belong to. When the module loads and registers its Alpine.data()
// components, it calls `window.activateDeferred('<name>')` to:
//   1. Initialize any matching elements already in the DOM
//   2. Mark that name as ready so the MutationObserver can initialize
//      future elements immediately when they appear
//
// The MutationObserver is attached to <html> (documentElement) rather
// than <body> because Turbo replaces the <body> element entirely on
// page visits (document.body.replaceWith(newBody)), which would
// disconnect an observer on the old body node.
// ---------------------------------------------------------------------------

const _deferredReady = new Set();

window.activateDeferred = function (name) {
  _deferredReady.add(name);
  document.querySelectorAll(`[data-alpine-defer="${name}"][x-ignore]`).forEach((el) => {
    el.removeAttribute('x-ignore');
    el._x_ignore = false;
    Alpine.initTree(el);
  });
};

const _deferObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;

      // Handle deferred Alpine components (lazy-loaded modules)
      const initIfReady = (el) => {
        const name = el.getAttribute?.('data-alpine-defer');
        if (name && _deferredReady.has(name) && el.hasAttribute('x-ignore')) {
          el.removeAttribute('x-ignore');
          el._x_ignore = false;
          Alpine.initTree(el);
        }
      };
      initIfReady(node);
      node.querySelectorAll?.('[data-alpine-defer][x-ignore]').forEach(initIfReady);
    }
  }
});
_deferObserver.observe(document.documentElement, { childList: true, subtree: true });

Alpine.plugin(anchor);
Alpine.plugin(morph);
Alpine.plugin(DragSort);
Alpine.start();

// ---------------------------------------------------------------------------
// Alpine + ring-hot-reload compatibility.
//
// Use Alpine.morph instead of Idiomorph for DOM morphing so that Alpine's
// reactive state (x-data, x-show, etc.) is preserved across hot reloads.
// ---------------------------------------------------------------------------
document.addEventListener('hot-reload:before-morph', (e) => {
  const doc = new DOMParser().parseFromString(e.detail.html, 'text/html');
  Alpine.morph(document.body, doc.body);
  e.preventDefault();
});
